import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Alert, Input } from '../../ui/components';
import { FileCheck, Bot, Clock, ShieldCheck } from 'lucide-react';
import { authService } from '../../services/supabaseService';
import { OwenAvatar } from '../../components/Owen';

const UnderwriterLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDemoMode = () => {
    // Bypass auth for demo purposes
    navigate('/uw/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.signIn(email, password);
      navigate('/uw/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const FeatureRow = ({ icon: Icon, title, desc }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', padding: 'var(--lk-space-3)', borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.1)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={24} />
      </div>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>{title}</div>
        <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.8 }}>{desc}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      {/* Left Side */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
      }}>
        <div style={{ maxWidth: 500, textAlign: 'center' }}>
          <div style={{ marginBottom: 'var(--lk-space-6)' }}>
            <OwenAvatar size="xlarge" showTooltip={false} />
          </div>
          <h1 style={{ fontSize: 'var(--lk-text-4xl)', fontWeight: 700, marginBottom: 'var(--lk-space-3)', letterSpacing: '-0.02em' }}>
            Underwriter Portal
          </h1>
          <p style={{ fontSize: 'var(--lk-text-lg)', opacity: 0.9, marginBottom: 'var(--lk-space-8)' }}>
            Review loan applications with AI-powered insights and streamlined workflows.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
            <FeatureRow icon={FileCheck} title="Comprehensive Reviews" desc="Access complete loan files and documentation" />
            <FeatureRow icon={Bot} title="AI-Powered Insights" desc="Owen AI assists with risk assessment" />
            <FeatureRow icon={Clock} title="Real-Time Updates" desc="Track application status and conditions" />
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
        background: 'white',
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-6)' }}>
            <h2 style={{ fontWeight: 700, marginBottom: 4 }}>Underwriter Sign In</h2>
            <p style={{ color: 'var(--lk-text-tertiary)', margin: 0 }}>Access your underwriting dashboard</p>
          </div>

          {error && (
            <Alert variant="error" style={{ marginBottom: 'var(--lk-space-3)' }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="underwriter@example.com"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              style={{ marginTop: 'var(--lk-space-3)' }}
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              style={{ width: '100%', marginTop: 'var(--lk-space-4)' }}
            >
              Sign In
            </Button>
          </form>

          <Surface variant="muted" padding="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-5)', textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginBottom: 'var(--lk-space-2)' }}>
              For demonstration purposes
            </div>
            <Button
              variant="secondary"
              style={{ width: '100%', borderStyle: 'dashed' }}
              onClick={handleDemoMode}
            >
              Enter Demo Mode
            </Button>
          </Surface>

          <p style={{ color: 'var(--lk-text-quaternary)', textAlign: 'center', fontSize: 'var(--lk-text-xs)', marginTop: 'var(--lk-space-4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <ShieldCheck size={12} />
            Secure underwriter access powered by Owen AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnderwriterLoginPage;
